// CommandPalette — ⌘K global-search overlay. A grouped, keyboard-navigable
// command/search list in a centered scrim (the prototype's CommandPalette).
// Presentational + self-contained: the caller supplies `groups` and handles
// `onSelect`; the palette owns its own query, filtering, selection index,
// keyboard nav (↑/↓/↵/esc), focus, and body scroll-lock. No data/route
// knowledge lives here, so it stays a generic reusable (Frontend Rules §4).
import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { cx } from '../../../utils/cx';
import { useScrollLock } from '../../../hooks';
import { SearchIcon } from '../../icons/Icons';
import './command-palette.css';

export function CommandPalette({
  open,
  onClose,
  onSelect,
  groups = [],
  placeholder,
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [selIdx, setSelIdx] = useState(0);
  const inputRef = useRef(null);

  useScrollLock(open);

  // Reset query + selection and focus the input each time the palette opens.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelIdx(0);
    // Defer focus so the element is mounted/painted first.
    const timer = setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
    return () => clearTimeout(timer);
  }, [open]);

  // Filter each group by title; drop empty groups. `flat` is the linear list the
  // arrow keys walk; `selIdx` is clamped to it.
  const filteredGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return groups
      .map((g) => ({
        ...g,
        items: q ? g.items.filter((i) => i.title.toLowerCase().includes(q)) : g.items,
      }))
      .filter((g) => g.items.length > 0);
  }, [groups, query]);

  const flat = useMemo(() => filteredGroups.flatMap((g) => g.items), [filteredGroups]);
  const clampedSel = Math.min(selIdx, Math.max(0, flat.length - 1));

  // Keyboard navigation while open. Esc closing is also handled here so the
  // palette works even when focus is inside the input.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelIdx((i) => Math.min(flat.length - 1, i + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelIdx((i) => Math.max(0, i - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (flat.length > 0) onSelect(flat[clampedSel]);
        else onClose();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, flat, clampedSel, onSelect, onClose]);

  if (!open) return null;

  let runningIdx = -1;
  return (
    <div className="cmd-scrim" onMouseDown={onClose}>
      <div
        className="cmd-palette"
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="cmd-palette__input-row">
          <SearchIcon aria-hidden="true" />
          <input
            ref={inputRef}
            className="cmd-palette__input"
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelIdx(0);
            }}
            placeholder={placeholder ?? t('reusable.commandPalette.placeholder')}
            aria-label="Search"
          />
          <button type="button" className="cmd-palette__esc" onClick={onClose}>
            esc
          </button>
        </div>

        <div className="cmd-palette__results">
          {filteredGroups.length === 0 ? (
            <div className="cmd-palette__empty">No matches for &ldquo;{query}&rdquo;</div>
          ) : (
            filteredGroups.map((g) => (
              <div key={g.name} className="cmd-palette__group">
                <div className="cmd-palette__group-head">{g.name}</div>
                {g.items.map((item) => {
                  runningIdx += 1;
                  const idx = runningIdx;
                  const Ico = item.icon;
                  const isSel = idx === clampedSel;
                  return (
                    <button
                      key={item.id || item.title}
                      type="button"
                      className={cx('cmd-palette__item', isSel && 'is-selected')}
                      onMouseEnter={() => setSelIdx(idx)}
                      onClick={() => onSelect(item)}
                    >
                      <span className="cmd-palette__item-icon">{Ico ? <Ico /> : null}</span>
                      <span className="cmd-palette__item-text">
                        <span className="cmd-palette__item-title">{item.title}</span>
                        {item.sub && <span className="cmd-palette__item-sub">{item.sub}</span>}
                      </span>
                      {item.trail && <span className="cmd-palette__item-trail">{item.trail}</span>}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="cmd-palette__foot">
          <span className="cmd-palette__hint">
            <span className="cmd-palette__kbd">↑</span>
            <span className="cmd-palette__kbd">↓</span> navigate
          </span>
          <span className="cmd-palette__hint">
            <span className="cmd-palette__kbd">↵</span> select
          </span>
          <span className="cmd-palette__hint">
            <span className="cmd-palette__kbd">esc</span> close
          </span>
          <span className="cmd-palette__count">
            {flat.length} {flat.length === 1 ? 'result' : 'results'}
          </span>
        </div>
      </div>
    </div>
  );
}

const itemShape = PropTypes.shape({
  id: PropTypes.string,
  icon: PropTypes.elementType,
  title: PropTypes.string.isRequired,
  sub: PropTypes.string,
  trail: PropTypes.string,
});

CommandPalette.propTypes = {
  /** Whether the palette overlay is shown. */
  open: PropTypes.bool,
  /** Close the palette (backdrop click / esc / after select). */
  onClose: PropTypes.func.isRequired,
  /** Called with the chosen item when a result is selected. */
  onSelect: PropTypes.func.isRequired,
  /** Grouped results: [{ name, items: [{ icon, title, sub, trail }] }]. */
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      items: PropTypes.arrayOf(itemShape).isRequired,
    }),
  ),
  placeholder: PropTypes.string,
};

export default CommandPalette;
